import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { useNavigate } from "react-router-dom";
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb";
import NewsAndArticleApi from "Api/NewsAndArticleApi";
import { Toast } from "primereact/toast";
import "./ListNewsAndArticles.scss";

const ListNewsAndArticles = () => {
    const navigate = useNavigate();
    const [newsArticles, setNewsArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const toast = useRef(null);

    const breadcrumbItems = [
        { label: "Pixwik", link: "#" },
        { label: "News and Articles", link: "#" },
    ];

    useEffect(() => {
        const fetchNewsArticles = async () => {
            try {
                const response = await NewsAndArticleApi.getAllNewsAndArticles();
                if (response.data.success) {
                    setNewsArticles(response.data.data || []);
                } else {
                    setError(response.data.message || "Failed to fetch articles");
                    setNewsArticles([]);
                }
                setLoading(false);
            } catch (error) {
                console.error("Error fetching news articles:", error);
                setError(error.message || "Failed to fetch articles");
                setNewsArticles([]);
                setLoading(false);
            }
        };
        fetchNewsArticles();
    }, []);

    const handleAddNewsArticle = () => {
        navigate("/add-news-article");
    };

    const handleViewArticle = (id) => {
        navigate(`/news-and-articles/${id}`);
    };

    const handleDeleteArticle = async (id, e) => {
        e.stopPropagation();
        if (window.confirm("Are you sure you want to delete this article?")) {
            try {
                const response = await NewsAndArticleApi.deleteNewsAndArticle(id);
                if (response.data.success) {
                    setNewsArticles(newsArticles.filter(article => article._id !== id));
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Article deleted successfully',
                        life: 3000
                    });
                } else {
                    toast.current.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: response.data.message || 'Failed to delete article',
                        life: 3000
                    });
                }
            } catch (error) {
                console.error("Error deleting article:", error);
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.data?.message || 'Failed to delete article',
                    life: 3000
                });
            }
        }
    };

    const handleEditArticle = (id, e) => {
        e.stopPropagation();
        navigate(`/edit-news-article/${id}`);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    return (
        <div className="news-articles-list">
            <Toast ref={toast} />
            <Container>
                <Row>
                    <Col xs="12">
                        <div className="news-list-header d-flex align-items-center justify-content-between mb-4">
                            <CustomBreadCrumb
                                data={breadcrumbItems}
                                pageTitle="Recent News & Article"
                            />
                            <Button
                                type="button"
                                color="secondary"
                                className="waves-effect"
                                onClick={handleAddNewsArticle}
                            >
                                Add News & Article
                            </Button>
                        </div>

                        <Row>
                            {loading ? (
                                <Col xs="12">
                                    <div className="text-center">Loading...</div>
                                </Col>
                            ) : error ? (
                                <Col xs="12">
                                    <div className="text-center text-danger">{error}</div>
                                </Col>
                            ) : newsArticles.length === 0 ? (
                                <Col xs="12">
                                    <div className="text-center">No articles found</div>
                                </Col>
                            ) : (
                                newsArticles.map((article) => (
                                    <Col lg="4" md="6" key={article._id} className="mb-4">
                                        <Card className="news-card h-100" onClick={() => handleViewArticle(article._id)} style={{ cursor: 'pointer' }}>
                                            <div className="news-image">
                                                <img
                                                    src={article.image}
                                                    alt={article.title}
                                                    className="img-fluid"
                                                    onError={(e) => {
                                                        e.target.src = "/assets/images/placeholder.jpg";
                                                    }}
                                                />
                                            </div>
                                            <CardBody>
                                                <h5 className="news-title">{article.title}</h5>
                                                <div className="news-author">
                                                    <p className="mb-0">By {article?.authorDetails?.name} , {article?.authorDetails?.position}</p>
                                                </div>
                                                <p className="news-date">{formatDate(article.createdAt)}</p>
                                                <div className="d-flex justify-content-end gap-2">
                                                    <i 
                                                        className="mdi mdi-eye action-icon" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleViewArticle(article._id);
                                                        }}
                                                    />
                                                    <i 
                                                        className="mdi mdi-pencil action-icon" 
                                                        onClick={(e) => handleEditArticle(article._id, e)}
                                                    />
                                                    <i 
                                                        className="mdi mdi-delete action-icon text-danger" 
                                                        onClick={(e) => handleDeleteArticle(article._id, e)}
                                                    />
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                ))
                            )}
                        </Row>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default ListNewsAndArticles;
